import type { CMSIntroBlock } from '~/types/Contentful'
import { documentToHtmlString } from '@contentful/rich-text-html-renderer'

/**
 * Calculates the remaining points or streak needed to reach a required value.
 * Ensures the returned value is never negative.
 *
 * @param required - The required amount to reach.
 * @param current - The current amount achieved (default is 0).
 * @returns The difference needed to reach the required value, or 0 if already met.
 */
const getDifferenceNeeded = (required: number, current: number = 0): number =>
  Math.max(0, required - current)

/**
 * Defines the different types of awards a user can earn.
 */
export type AwardType = 'djurvan' | 'djurbastis' | 'djurbeskyddare' | 'djurkampe' | 'djurhjalte'

/**
 * Checks if a user has unlocked an award based on their score and streak.
 *
 * @param score - The user's total score.
 * @param requiredScore - The required score to unlock the award.
 * @param streak - The user's streak count.
 * @param requiredStreak - The required streak to unlock the award (default is 0).
 * @returns `true` if the user has met both the score and streak requirements, otherwise `false`.
 */
const isAwardEarned = (
  score: number,
  requiredScore: number,
  streak: number,
  requiredStreak: number = 0,
): boolean => score >= requiredScore && streak >= requiredStreak

interface AwardRule {
  title: string
  type: AwardType
  requiredScore: number
  requiredStreak?: number
}

/**
 * List of award rules defining the requirements to earn each award.
 */
export const awardRules: AwardRule[] = [
  { title: 'Djurvän', type: 'djurvan', requiredScore: 5 },
  { title: 'Djurbästis', type: 'djurbastis', requiredScore: 15 },
  { title: 'Djurbeskyddare', type: 'djurbeskyddare', requiredScore: 30, requiredStreak: 2 },
  { title: 'Djurkämpe', type: 'djurkampe', requiredScore: 60, requiredStreak: 3 },
  { title: 'Djurhjälte', type: 'djurhjalte', requiredScore: 100, requiredStreak: 4 },
]

interface ActionScore {
  score?: number
  weekScore?: { score?: number }
}

export interface Award {
  title: string
  type: AwardType
  text: string
  isUnlocked: boolean
  isLocked: boolean
  pointsNeeded: number
  streakNeeded: number
  isCurrent: boolean
  isNext: boolean
}

/**
 * Computes a list of awards for a user based on their current progress.
 *
 * @param actions - The user's current score and weekly streak.
 * @returns A list of awards with their respective statuses.
 */
export const getAwards = (actions: ActionScore = {}): Award[] => {
  const currentScore = actions?.score ?? 0
  const currentStreak = actions?.weekScore?.score ?? 0

  // Generate the list of awards with their current status
  const awardsList: Award[] = awardRules.map(
    ({ title, type, requiredScore, requiredStreak = 0 }) => {
      const isEarned = isAwardEarned(currentScore, requiredScore, currentStreak, requiredStreak)

      return {
        title,
        type,
        text: `${requiredScore} poäng${requiredStreak ? ` + ${requiredStreak} veckostreaks` : ''}`,
        isUnlocked: isEarned,
        isLocked: !isEarned,
        pointsNeeded: getDifferenceNeeded(requiredScore, currentScore),
        streakNeeded: getDifferenceNeeded(requiredStreak, currentStreak),
        isCurrent: false,
        isNext: false,
      }
    },
  )

  // Find the first locked award and mark it as "next"
  const nextLockedIndex = awardsList.findIndex(award => award.isLocked)
  if (nextLockedIndex !== -1) {
    awardsList[nextLockedIndex].isNext = true
  }

  // Find the last unlocked award and mark it as "current"
  const lastUnlockedIndex = awardsList
    .map((award, index) => (award.isUnlocked ? index : -1))
    .filter(index => index !== -1)
    .pop()

  if (lastUnlockedIndex !== undefined) {
    awardsList[lastUnlockedIndex].isCurrent = true
  }

  return awardsList
}

/**
 * Retrieves a list of all completed awards that the user has unlocked.
 *
 * @param actions - The user's current score and weekly streak.
 * @returns A filtered list of awards where `isUnlocked` is `true`.
 */
export const getCompletedAwards = (actions: ActionScore = {}): Award[] => {
  const allAwards = getAwards(actions)
  return allAwards.filter(award => award.isUnlocked)
}

/**
 * Predefined static images for different award categories.
 */
export const awardImages = {
  djurvan:
    'https://res.cloudinary.com/djurensratt/image/upload/v1738334258/djurensratt-se/illustrationer/djur/kycklingar.svg',
  djurbastis:
    'https://res.cloudinary.com/djurensratt/image/upload/v1738334258/djurensratt-se/illustrationer/djur/ko.svg',
  djurbeskyddare:
    'https://res.cloudinary.com/djurensratt/image/upload/v1738334259/djurensratt-se/illustrationer/djur/mink.svg',
  djurkampe:
    'https://res.cloudinary.com/djurensratt/image/upload/v1738335727/djurensratt-se/illustrationer/djur/hona_djurkampe.svg',
  djurhjalte:
    'https://res.cloudinary.com/djurensratt/image/upload/v1738335727/djurensratt-se/illustrationer/djur/gris_djurhjalte.svg',
}

/**
 * Transforms CMSIntroBlock data into a formatted HTML structure.
 *
 * @param data - The CMSIntroBlock data object.
 * @returns A formatted content block or null if the data is missing.
 */
export function transformIntroBlock(data: CMSIntroBlock | undefined) {
  if (!data || !data.fields) {
    return null
  }

  const { fields } = data

  // Extract subtitle and title
  const subtitle: string = fields?.subtitle || ''
  const title: string = fields?.title || ''

  // Extract and format copy and body content
  const copy = fields?.copy ? documentToHtmlString(fields.copy) : ''
  const body = fields?.body ? documentToHtmlString(fields.body) : ''

  return {
    id: 'reward-intro-block',
    position: 'left',
    subtitle: subtitle,
    title,
    copy,
    body,
  }
}

/**
 * Represents video data for an award badge.
 */
interface VideoData {
  id: string
  video: {
    url: string
    file: string
    params?: string
  }
}

/**
 * Default video placeholder used when no valid video is found.
 */
const defaultVideo: VideoData = {
  id: '',
  video: {
    url: '',
    file: '',
  },
}

/**
 * Retrieves the video associated with an award badge.
 * Returns a default video if no URL is provided.
 *
 * @param type - The award type.
 * @param url - The video URL.
 * @returns The corresponding VideoData.
 */
export const getBadgeVideo = (type: AwardType, url?: string): VideoData => {
  if (!url) {
    return defaultVideo
  }
  return {
    id: `video-${type}`,
    video: {
      url,
      file: '',
      params: '&modestbranding=1&controls=0&disablekb=1',
    },
  }
}
